import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, List, Tag, Button, Modal, Input, Empty,
  Space, Typography, Spin, message, Badge, Divider, Upload,
} from 'antd';
import type { UploadFile } from 'antd';
import {
  MessageOutlined, PlusOutlined, SendOutlined, CheckCircleOutlined,
  ClockCircleOutlined, StopOutlined, PaperClipOutlined,
} from '@ant-design/icons';
import { partnerAPI } from '../services/api';
import InquiryWriteModal, { CONTENT_MAX } from './InquiryWriteModal';
import AttachmentList, { Attachment } from './AttachmentList';
import { fmtDateTime } from '../utils/date';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;


export interface InquiryReply {
  id: number;
  author_type: 'admin' | 'partner';
  author_name: string;
  content: string;
  created_at: string;
  attachments?: Attachment[];
}

export interface Inquiry {
  id: number;
  schedule_id?: number;
  test_number: string;
  schedule_title: string;
  inquiry_type: string;
  title: string;
  content: string;
  status: string;
  partner_unread: boolean;
  reply_count: number;
  last_reply_at?: string;
  created_at: string;
  attachment_count?: number;
  attachments?: Attachment[];
  replies?: InquiryReply[];
}

export const ATTACH_MAX_BYTES = 20 * 1024 * 1024;

/** 드롭된 File[] → antd fileList 항목 (용량 초과분은 제외하고 안내) */
export const filesToUploadItems = (files: File[]): UploadFile[] => {
  const items: UploadFile[] = [];
  files.forEach((file, i) => {
    if (file.size > ATTACH_MAX_BYTES) {
      message.error(`${file.name}: 20MB를 초과할 수 없습니다.`);
      return;
    }
    items.push({
      uid: `drop-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      originFileObj: file as any,
    });
  });
  return items;
};

export const INQUIRY_STATUS_META: Record<string, { color: string; icon: React.ReactNode }> = {
  '대기':     { color: 'orange', icon: <ClockCircleOutlined /> },
  '답변완료': { color: 'green',  icon: <CheckCircleOutlined /> },
  '수용':     { color: 'blue',   icon: <CheckCircleOutlined /> },   // 요청 반영완료
  '반려':     { color: 'red',    icon: <StopOutlined /> },
  '종료':     { color: 'default', icon: <StopOutlined /> },
};

/* 일시 표기는 공용 유틸이 정본 — 여기서 다시 만들지 않는다 (utils/date.ts) */
export const formatDateTime = (v?: string) => fmtDateTime(v);

interface Props {
  /** 지정 시 해당 시험일정 전용 (일정 선택 없이 고정) */
  scheduleId?: number;
  scheduleLabel?: string;
  /** 카드 없이 내용만 렌더링 */
  bare?: boolean;
  /** 목록/스레드 변경 시 상위 배지 갱신용 */
  onChanged?: () => void;
}

const InquirySection: React.FC<Props> = ({ scheduleId, scheduleLabel, bare, onChanged }) => {
  const [items, setItems] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [writeOpen, setWriteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [detail, setDetail] = useState<Inquiry | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState<UploadFile[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnerAPI.inquiries(scheduleId ? { schedule_id: scheduleId } : undefined);
      setItems(res.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => { load(); }, [load]);

  const reloadDetail = async (id: number) => {
    try {
      const res = await partnerAPI.inquiryDetail(id);
      setDetail(res.data);
    } catch { /* 유지 */ }
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setDetail({ id } as Inquiry);
    setReplyText('');
    setReplyFiles([]);
    try {
      const res = await partnerAPI.inquiryDetail(id);
      setDetail(res.data);
      setItems(prev => prev.map(it => (it.id === id ? { ...it, partner_unread: false } : it)));
      onChanged?.();
    } catch {
      message.error('문의를 불러오지 못했습니다.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitReply = async () => {
    if (!detail || !replyText.trim()) return;
    setSubmitting(true);
    try {
      const res = await partnerAPI.replyInquiry(detail.id, replyText.trim());
      // 방금 등록한 답글에 첨부파일 연결
      const newReplyId = (res.data.replies || []).slice(-1)[0]?.id;
      const failed: string[] = [];
      for (const f of replyFiles) {
        try {
          await partnerAPI.uploadInquiryFile(detail.id, (f.originFileObj || f) as File, newReplyId);
        } catch {
          failed.push(f.name);
        }
      }
      if (failed.length) message.warning(`첨부 실패: ${failed.join(', ')}`);
      setReplyText('');
      setReplyFiles([]);
      if (replyFiles.length) await reloadDetail(detail.id);
      else setDetail(res.data);
      load();
      onChanged?.();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const listBody = loading ? (
    <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
  ) : items.length === 0 ? (
    <Empty description="등록된 문의/요청이 없습니다" image={Empty.PRESENTED_IMAGE_SIMPLE} />
  ) : (
    <List
      dataSource={items}
      renderItem={(item) => {
        const meta = INQUIRY_STATUS_META[item.status] || INQUIRY_STATUS_META['대기'];
        return (
          <List.Item
            style={{
              padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
              background: item.partner_unread ? '#fffbeb' : 'transparent',
              border: item.partner_unread ? '1px solid #fde68a' : '1px solid transparent',
              marginBottom: 6,
            }}
            onClick={() => openDetail(item.id)}
          >
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                <Tag color={item.inquiry_type === '요청' ? 'purple' : 'blue'} style={{ margin: 0, fontWeight: 600 }}>
                  {item.inquiry_type}
                </Tag>
                <Text strong={item.partner_unread} style={{ fontSize: 13 }}>{item.title}</Text>
                {item.partner_unread && <Badge count="NEW" style={{ backgroundColor: '#f59e0b', fontSize: 10 }} />}
                <div style={{ marginLeft: 'auto' }}>
                  <Tag color={meta.color} icon={meta.icon} style={{ margin: 0 }}>{item.status}</Tag>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#94a3b8', flexWrap: 'wrap' }}>
                {item.test_number && <span>시험번호 <b style={{ color: '#b45309' }}>{item.test_number}</b></span>}
                {item.schedule_title && <span style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.schedule_title}</span>}
                <span>등록 {formatDateTime(item.created_at)}</span>
                <span>답글 {item.reply_count}건</span>
                {!!item.attachment_count && (
                  <span><PaperClipOutlined /> {item.attachment_count}</span>
                )}
              </div>
            </div>
          </List.Item>
        );
      }}
    />
  );

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <MessageOutlined style={{ color: '#d97706' }} />
      <span style={{ fontWeight: 700, color: '#92400e' }}>문의 / 요청</span>
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setWriteOpen(true)} style={{ marginLeft: 'auto' }}>
        새 문의 작성
      </Button>
    </div>
  );

  return (
    <>
      {bare ? (
        <div>
          <div style={{ marginBottom: 12 }}>{header}</div>
          {listBody}
        </div>
      ) : (
        <Card size="small" title={header} styles={{ header: { borderBottom: '1px solid #fef3c7' } }}>
          {listBody}
        </Card>
      )}

      <InquiryWriteModal
        open={writeOpen}
        onCancel={() => setWriteOpen(false)}
        onCreated={() => { load(); onChanged?.(); }}
        scheduleId={scheduleId}
        scheduleLabel={scheduleLabel}
      />

      {/* 상세/스레드 모달 */}
      <Modal
        title={
          <Space>
            <MessageOutlined style={{ color: '#f59e0b' }} />
            <span style={{ fontWeight: 700 }}>{detail?.title || '문의 상세'}</span>
            {detail?.status && (
              <Tag color={(INQUIRY_STATUS_META[detail.status] || INQUIRY_STATUS_META['대기']).color}>
                {detail.status}
              </Tag>
            )}
          </Space>
        }
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={<Button onClick={() => setDetail(null)}>닫기</Button>}
        width={640}
      >
        {detailLoading || !detail?.created_at ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>
              {detail.test_number && <>시험번호 <b style={{ color: '#b45309' }}>{detail.test_number}</b> · </>}
              {detail.schedule_title && <>{detail.schedule_title} · </>}
              등록 {formatDateTime(detail.created_at)}
            </div>

            {/* 최초 문의 */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                <Tag color={detail.inquiry_type === '요청' ? 'purple' : 'blue'} style={{ marginRight: 6 }}>{detail.inquiry_type}</Tag>
                우리회사 문의
              </div>
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>{detail.content}</Paragraph>
              <AttachmentList
                items={detail.attachments}
                canDelete
                onDeleted={() => reloadDetail(detail.id)}
              />
            </div>

            {/* 답글 스레드 */}
            {(detail.replies || []).map((r) => {
              const isAdmin = r.author_type === 'admin';
              return (
                <div
                  key={r.id}
                  style={{
                    background: isAdmin ? '#fffbeb' : '#f8fafc',
                    border: `1px solid ${isAdmin ? '#fde68a' : '#e2e8f0'}`,
                    borderRadius: 10, padding: 12, marginBottom: 10,
                    marginLeft: isAdmin ? 0 : 28, marginRight: isAdmin ? 28 : 0,
                  }}
                >
                  <div style={{ fontSize: 11, color: isAdmin ? '#b45309' : '#64748b', marginBottom: 6, fontWeight: 600 }}>
                    {isAdmin ? `SAPPT 담당자 (${r.author_name})` : `우리회사 (${r.author_name})`}
                    <span style={{ fontWeight: 400, marginLeft: 8, color: '#94a3b8' }}>{formatDateTime(r.created_at)}</span>
                  </div>
                  <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>{r.content}</Paragraph>
                  <AttachmentList
                    items={r.attachments}
                    canDelete={!isAdmin}
                    onDeleted={() => reloadDetail(detail.id)}
                    compact
                  />
                </div>
              );
            })}

            {detail.status !== '종료' && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                {/* 작성 영역 어디에나 파일을 끌어다 놓으면 첨부된다 */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                  onDrop={(e) => {
                    e.preventDefault(); setDragOver(false);
                    const dropped = Array.from(e.dataTransfer?.files || []);
                    if (dropped.length) setReplyFiles((prev) => [...prev, ...filesToUploadItems(dropped)]);
                  }}
                  style={{
                    border: `2px dashed ${dragOver ? '#f59e0b' : 'transparent'}`,
                    background: dragOver ? '#fffbeb' : 'transparent',
                    borderRadius: 8, padding: 6, transition: 'all .15s',
                  }}
                >
                <TextArea
                  rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                  placeholder="추가 문의 내용을 입력하세요. (파일을 이곳에 끌어다 놓으면 첨부됩니다)"
                  maxLength={CONTENT_MAX} showCount
                />
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8 }}>
                  <Upload
                    multiple
                    fileList={replyFiles}
                    beforeUpload={(file) => {
                      if (file.size > ATTACH_MAX_BYTES) {
                        message.error(`${file.name}: 20MB를 초과할 수 없습니다.`);
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    }}
                    onChange={({ fileList: fl }) => setReplyFiles(fl)}
                    onRemove={(file) => setReplyFiles((prev) => prev.filter((f) => f.uid !== file.uid))}
                  >
                    <Button size="small" icon={<PaperClipOutlined />}>파일 첨부</Button>
                  </Upload>
                  <div style={{ marginLeft: 'auto' }}>
                    <Button
                      type="primary" icon={<SendOutlined />} loading={submitting}
                      disabled={!replyText.trim()} onClick={submitReply}
                    >
                      추가 문의 등록
                    </Button>
                  </div>
                </div>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default InquirySection;
